import XDate from 'xdate'

import {
  QuickPassSavedSearch,
  QuickPassSavedSearchCriteria,
  QuickPassSavedSearchFrequency,
} from '../../_types/quickpass-saved-search'
import { defaultQuickPassSavedSearchCriteria } from '../../_defaults/quickpass-saved-search'
import { QuickPassProfile } from '../../_types/quickpass-profile'
import { SavedSearchFormResult, SavedSearchFormState } from './types'

const FREQUENCY_OPTIONS: {
  value: QuickPassSavedSearchFrequency
  label: string
}[] = [
  { value: 'instant', label: 'Tell me right away' },
  { value: 'daily', label: 'Daily round-up' },
  { value: 'weekly', label: 'Weekly digest' },
]

const buildDefaultFormState = (): SavedSearchFormState => {
  return {
    name: '',
    citiesText: '',
    priceMinText: '',
    priceMaxText: '',
    bedroomsText: '',
    bathroomsText: '',
    frequency: 'daily',
    notifyOnMatch: true,
  }
}

const buildFormStateFromProfile = (
  profile: QuickPassProfile | null,
): SavedSearchFormState => {
  if (profile === null) {
    return buildDefaultFormState()
  }

  const preferences = profile.unitPreferences
  const giveName = profile.firstName !== '' ? `${profile.firstName}'s ` : ''

  return {
    name: `${giveName}top picks`,
    citiesText: preferences.cities.join(', '),
    priceMinText:
      preferences.priceMin !== null ? String(preferences.priceMin) : '',
    priceMaxText:
      preferences.priceMax !== null ? String(preferences.priceMax) : '',
    bedroomsText: preferences.bedroomCounts.join(', '),
    bathroomsText: preferences.bathroomCounts.join(', '),
    frequency: 'daily',
    notifyOnMatch: true,
  }
}

const parseCommaSeparatedStrings = (value: string): string[] => {
  return value
    .split(',')
    .map(entry => entry.trim())
    .filter(entry => entry !== '')
}

const parseCommaSeparatedNumbers = (value: string): number[] => {
  return parseCommaSeparatedStrings(value)
    .map(entry => Number.parseFloat(entry))
    .filter(parsed => Number.isFinite(parsed))
}

const parseOptionalNumber = (value: string): number | null => {
  if (value.trim() === '') {
    return null
  }
  const parsed = Number.parseFloat(value)
  if (Number.isFinite(parsed) === false) {
    return null
  }
  return parsed
}

/**
 * Converts the form state into the typed criteria + frequency the data
 * layer needs. Returns `null` when validation fails — the only required
 * field is `name`.
 *
 * @param {SavedSearchFormState} state - the live form state
 * @returns {SavedSearchFormResult | null}
 */
const finaliseFormState = (
  state: SavedSearchFormState,
): SavedSearchFormResult | null => {
  const trimmedName = state.name.trim()

  if (trimmedName === '') {
    return null
  }

  const criteria: QuickPassSavedSearchCriteria = {
    ...defaultQuickPassSavedSearchCriteria,
    cities: parseCommaSeparatedStrings(state.citiesText),
    priceMin: parseOptionalNumber(state.priceMinText),
    priceMax: parseOptionalNumber(state.priceMaxText),
    bedroomCounts: parseCommaSeparatedNumbers(state.bedroomsText),
    bathroomCounts: parseCommaSeparatedNumbers(state.bathroomsText),
  }

  return {
    name: trimmedName,
    criteria,
    frequency: state.frequency,
    notifyOnMatch: state.notifyOnMatch,
  }
}

/**
 * Builds a one-line summary of the criteria for the saved-search row.
 *
 * @param {QuickPassSavedSearch} savedSearch
 * @returns {string}
 */
const summariseCriteria = (savedSearch: QuickPassSavedSearch): string => {
  const segments: string[] = []
  const criteria = savedSearch.criteria

  if (criteria.cities.length > 0) {
    segments.push(criteria.cities.join(' / '))
  }

  if (criteria.priceMin !== null || criteria.priceMax !== null) {
    const minPart =
      criteria.priceMin !== null
        ? `$${criteria.priceMin.toLocaleString('en-CA')}`
        : ''
    const maxPart =
      criteria.priceMax !== null
        ? `$${criteria.priceMax.toLocaleString('en-CA')}`
        : ''
    if (minPart !== '' && maxPart !== '') {
      segments.push(`${minPart}–${maxPart}/mo`)
    } else if (minPart !== '') {
      segments.push(`${minPart}+/mo`)
    } else {
      segments.push(`up to ${maxPart}/mo`)
    }
  }

  if (criteria.bedroomCounts.length > 0) {
    segments.push(`${criteria.bedroomCounts.join('/')} BR`)
  }

  if (criteria.bathroomCounts.length > 0) {
    segments.push(`${criteria.bathroomCounts.join('/')} BA`)
  }

  if (segments.length === 0) {
    return 'No filters — matches everything you import.'
  }

  return segments.join(' · ')
}

/**
 * Formats the `lastRunAt` timestamp into a relative-ish label, or "Never"
 * when the search has never been executed.
 *
 * @param {number | null} lastRunAt
 * @returns {string}
 */
const formatLastRun = (lastRunAt: number | null): string => {
  if (lastRunAt === null) {
    return 'Never run'
  }

  const lastRunDate = new XDate(lastRunAt)
  return `Last run ${lastRunDate.toString("MMM d 'at' h:mm TT")}`
}

export {
  FREQUENCY_OPTIONS,
  buildDefaultFormState,
  buildFormStateFromProfile,
  finaliseFormState,
  summariseCriteria,
  formatLastRun,
}
